import styled from "styled-components";

export const GRID_LINES_DISPLAY = {
  none: "none",
  horizontal: "horizontal",
  vertical: "vertical",
  all: "horizontal and vertical",
} as const;

export const DEFAULT_GRIDLINE_COLOR = "rgba(153, 153, 153, 1)";

export type GridLinesDisplayValue = (typeof GRID_LINES_DISPLAY)[keyof typeof GRID_LINES_DISPLAY];

const GRIDLINE_DISPLAY_CLASS: Record<GridLinesDisplayValue, string> = {
  [GRID_LINES_DISPLAY.none]: "table-gridline-none",
  [GRID_LINES_DISPLAY.all]: "",
  [GRID_LINES_DISPLAY.horizontal]: "table-gridline-horizontal",
  [GRID_LINES_DISPLAY.vertical]: "table-gridline-vertical",
};

export const getGridlineDisplayClass = (display: unknown): string => {
  if (typeof display === "string" && display in GRIDLINE_DISPLAY_CLASS) {
    return GRIDLINE_DISPLAY_CLASS[display as GridLinesDisplayValue];
  }
  return GRIDLINE_DISPLAY_CLASS[GRID_LINES_DISPLAY.all];
};

const handleStyledCellBorder = (
  totalRows: number,
  rowIndex: number,
  colIndex: number,
  rowSpan: number,
  gridlines: { display?: string; color?: string },
) => {
  const display = gridlines?.display || GRID_LINES_DISPLAY.all;
  const color = gridlines?.color || DEFAULT_GRIDLINE_COLOR;
  const lastRow = totalRows - 1;
  // A row-spanning cell starts above the last row but still occupies it.
  const occupiesLastRow = rowIndex <= lastRow && lastRow <= rowIndex + (rowSpan || 1) - 1;
  const borderBottom = occupiesLastRow ? `border-bottom: 1px solid ${color};` : "";
  switch (display) {
    case GRID_LINES_DISPLAY.all:
      return `
        border-top: 1px solid ${color};
        border-left: 1px solid ${color};
        ${borderBottom}
      `;
    case GRID_LINES_DISPLAY.none:
      return `
      ${colIndex !== 0 && rowIndex !== 0 ? "border:none;" : ""}
      ${colIndex === 0 ? `border-left: 1px solid ${color};` : ""}
      ${rowIndex === 0 ? `border-top: 1px solid ${color};` : ""}
      ${borderBottom}
      `;
    case GRID_LINES_DISPLAY.horizontal:
      return `
      border-top: 1px solid ${color};
      ${colIndex === 0 ? `border-left: 1px solid ${color};` : ""}
      ${borderBottom}
      `;
    case GRID_LINES_DISPLAY.vertical:
      return `
      border-left: 1px solid ${color};
      ${borderBottom}
      `;
    default:
      return "";
  }
};

// BV parity: the SPF table plugin's label section is a bare h4 (children
// inline-block) with no padding or margin of its own, so the theme's
// span.component-title margin-bottom (0.85em) collapses with the theme's
// "body h4" -8px bottom margin into a ~5.6px gap above the grid. The shared
// LabelSection's padding-bottom: 10px blocks that collapse and adds 10px,
// leaving ~11px more gap than BV.
export const TableLabelSection = styled.h4`
  & > * {
    display: inline-block;
  }

  & > .component-label > p,
  & > .component-label > span > p,
  & > .component-title > p,
  & > .component-title > span > p {
    margin: 0;
  }

  & > .component-label span svg,
  & > .component-title span svg {
    vertical-align: middle;
  }
`;

export const TableContainer = styled.div<{ $headerColor?: string; $numCols?: number }>`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  color: var(--text-color, inherit);
  font-family: var(--body-font, inherit);

  table {
    grid-template-columns: repeat(${({ $numCols }) => $numCols || 1}, 1fr);
  }

  @media (max-width: 480px) {
    padding-bottom: 20px;
  }

  ${(props) =>
    props.$headerColor
      ? `
    th {
      background-color: ${props.$headerColor};
    }
  `
      : ""}
`;

/** SPF uses className "component-instruction" (singular), not "component-instructions". */
export const TableInstructions = styled.div.attrs({
  className: "component-instruction",
})`
  margin-bottom: 8px;

  & > p span svg {
    vertical-align: middle;
  }
`;

export const StyledTable = styled.table<{
  $numCols: number;
  $rowsSize: number;
  $gridlinesDisplay?: string;
  $gridlinesColor?: string;
  $horizontalScroll?: boolean;
}>`
  min-width: min-content;
  width: 100%;
  border-collapse: collapse;
  display: grid;
  grid-template-columns: repeat(${({ $numCols }) => $numCols}, 1fr);
  grid-template-rows: repeat(${({ $rowsSize }) => $rowsSize}, auto);
  color: inherit;

  tbody,
  tfoot,
  thead,
  tr {
    display: contents;
  }

  td:has(div.spf-inline-expression) {
    min-width: max-content;
  }

  ${(props) =>
    props.$horizontalScroll
      ? `
    @media (max-width: 480px) {
      min-width: 800px;
    }
  `
      : ""}

  &::after {
    content: "";
    grid-column: 1 / -1;
    grid-row: 1 / -1;
    border: 1px solid ${({ $gridlinesColor }) => $gridlinesColor || DEFAULT_GRIDLINE_COLOR};
    pointer-events: none;
  }
`;

export const StyledCell = styled.td<{
  $isHeader?: boolean;
  $horizontalAlign?: string;
  $headerColor?: string;
  $rowIndex: number;
  $colIndex: number;
  $rowSpan?: number;
  $totalRows: number;
  $gridlinesDisplay?: string;
  $gridlinesColor?: string;
}>`
  outline: none;
  box-sizing: border-box;
  padding: 5px;
  color: var(--text-color, inherit);
  word-break: normal;
  text-align: ${(props) => {
    if (props.$horizontalAlign === "center") return "center";
    if (props.$horizontalAlign === "end" || props.$horizontalAlign === "right") {
      return "right";
    }
    return "left";
  }};
  ${(props) => {
    if (props.$isHeader && props.$headerColor) {
      return `background-color: ${props.$headerColor};`;
    }
    if (!props.$isHeader) {
      return "background-color: var(--surface-color, transparent);";
    }
    return "";
  }}
  ${(props) =>
    handleStyledCellBorder(props.$totalRows, props.$rowIndex, props.$colIndex, props.$rowSpan ?? 1, {
      display: props.$gridlinesDisplay,
      color: props.$gridlinesColor,
    })}

  & > div[data-slate-editor="true"] {
    height: 100%;
    display: grid;
    margin: 0 -0.5px 0 0;
    outline: none;
  }

  sub,
  sup {
    line-height: 0;
  }
`;

export const StyledCaption = styled.caption<{
  $isTitle?: boolean;
  $row?: number;
  $belowTable?: boolean;
}>`
  word-break: break-all;
  grid-column: 1 / -1;
  grid-row: ${(props) => props.$row};
  ${({ $belowTable }) => ($belowTable ? "text-align: left;" : "")}
  color: var(--text-color, inherit);

  ${(props) =>
    props.$isTitle
      ? `
    font-family: var(--title-font, inherit);
    font-weight: bolder;
    font-size: 1.17em;
  `
      : ""}

  ${({ $belowTable }) =>
    $belowTable &&
    `
    margin-top: 8px !important;
  `}

  colgroup ~ & {
    margin-bottom: 8px !important;
  }

  margin-bottom: 8px !important;
  margin-left: 8px !important;
  margin-right: 8px !important;

  p {
    margin: 0;

    span svg {
      vertical-align: middle;
    }
  }

  &.table-title p,
  &.table-caption p {
    justify-content: center;
  }
`;

export const StyledColGroup = styled.colgroup<{ $numRows: number }>`
  display: contents;

  col {
    grid-row: 1 / span ${({ $numRows }) => $numRows};
  }
`;
